<template>
  <div class="location-filter">
    <!-- MAIN ICON + LABEL -->
    <div class="location-icon-container" @click="handleIconClick">
      <div class="row">
        <div class="location-icon">
          <svg class="location-icon verySmall">
            <use href="@/assets/icons/iconset.svg#location_target"></use>
          </svg>
        </div>

        <!-- Inline Autocomplete Input -->
        <div v-if="showCityInput" class="city-input-container">
          <div class="input-wrapper">
            <input
              ref="cityInputRef"
              placeholder="Enter city name"
              v-model="typedCity"
              @input="handleCityInput"
              @keydown.tab.prevent="acceptPrediction"
              @blur="autoAcceptOnBlur"
              @click="maybeAcceptSuffix"
            />
            <!-- Grey suffix if exactly one match -->
            <span class="prediction-overlay" v-if="predictionSuffix">
              <span class="typed">{{ typedCity }}</span
              ><span class="suffix">{{ predictionSuffix }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- If filter active, show label with distance + city -->
      <div v-if="isLocationFilterActive" class="location-label">
        <div class="row alignBaselineRow tinyGap">
          <span class="noWrap">{{ searchRadius }} km from</span>
          <span class="noWrap selectedCity" @click="resetCitySelection">{{
            selectedCity || "Current location"
          }}</span>
        </div>
        <div v-if="userLocation" class="coordinates-box">
          <span class="noWrap"
            >Lat: {{ userLocation.lat.toFixed(3) }} | Lng:
            {{ userLocation.lng.toFixed(3) }}</span
          >
        </div>
      </div>

      <!-- If geoloc is set, show lat/lng -->
    </div>

    <!-- Circle Overlay -->
    <div
      class="circle-container"
      :class="{ 'is-animating': animateCircle }"
      v-show="isExpanded"
    >
      <div
        class="distance-circle"
        :style="{ width: circleSize + 'px', height: circleSize + 'px' }"
        @mousedown.stop="startDrag"
        @mousemove.stop="onDrag"
        @mouseup.stop="stopDrag"
        @touchstart.stop="startDrag"
        @touchmove.stop="onDrag"
        @touchend.stop="stopDrag"
      >
        <!-- Text near top or center -->
        <div class="distanceText">
          <span v-if="!isDragging && !showDistanceIndicator">
            Click and drag to filter by distance
          </span>
          <span v-else>{{ currentDistance }} km from</span>
        </div>

        <!-- Purple outline circle that grows/shrinks -->
        <div
          v-if="isDragging || showDistanceIndicator"
          class="purple-outline"
          :style="outlineCircleStyle"
        ></div>

        <!-- Centre dot -->
        <div class="location-center"></div>

        <!-- Above the dot: city/currentLocation or lat/lng -->
        <div class="center-label">
          {{ centerLabel }}
        </div>

        <!-- The line from centre to pointer -->
        <div
          class="distance-line"
          v-if="isDragging || showDistanceIndicator || showLineIfReopen"
          :style="lineOrZeroAngle"
        ></div>

        <!-- The bubble near the pointer (no text inside) -->
        <div
          class="distance-bubble"
          v-if="isDragging || showDistanceIndicator || showLineIfReopen"
          :style="selectorStyle"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";

export default {
  name: "LocationFilterComponent",
  emits: ["update:locationFilter"],
  setup(props, { emit }) {
    // ---------------------------------------------------------
    // Basic location filter states
    // ---------------------------------------------------------
    const isLocationFilterActive = ref(false);
    const userLocation = ref(null);
    const selectedCity = ref("");
    const hasLocationPermission = ref(false);

    // Show input if geoloc denied
    const showCityInput = ref(false);

    // Typed text for inline autocomplete
    const typedCity = ref("");
    const cityInputRef = ref(null);

    // Mock city list
    const mockCities = [
      { name: "New York", lat: 40.7128, lng: -74.006 },
      { name: "London", lat: 51.5074, lng: -0.1278 },
      { name: "Paris", lat: 48.8566, lng: 2.3522 },
      { name: "Tokyo", lat: 35.6762, lng: 139.6503 },
      { name: "Sydney", lat: -33.8688, lng: 151.2093 },
    ];

    // Found matches for the typed text
    const matchingCities = ref([]);

    // Circle states
    const isExpanded = ref(false);
    const animateCircle = ref(false);
    const circleSize = ref(280);

    const currentDistance = ref(10);
    const isDragging = ref(false);
    const showDistanceIndicator = ref(false);
    const selectorPosition = ref(null);
    const showLineIfReopen = ref(false);

    // Distance range
    const MIN_DISTANCE = 0.1;
    const MAX_DISTANCE = 20000;

    // Chosen radius
    const searchRadius = ref(10);

    // Re-emit changes
    watch(isLocationFilterActive, emitLocationFilter);

    // A small label above the center dot:
    // city name if we have one, else "Current location" if no city but geoloc,
    // else lat/lng if we want. We'll define a simple logic:
    const centerLabel = computed(() => {
      if (selectedCity.value) {
        return selectedCity.value;
      } else if (userLocation.value && !selectedCity.value) {
        return `Lat: ${userLocation.value.lat.toFixed(
          2
        )}, Lng: ${userLocation.value.lng.toFixed(2)}`;
      }
      return "Current location"; // fallback if nothing
    });

    // ----------------------------------------------------------------
    // Geolocation or fallback
    // ----------------------------------------------------------------
    function handleIconClick() {
      if (!hasLocationPermission.value && !selectedCity.value) {
        requestLocation();
      } else {
        expandCircle();
      }
    }

    function requestLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            userLocation.value = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            };
            hasLocationPermission.value = true;
            isLocationFilterActive.value = true;
            expandCircle();
          },
          () => {
            showCityInput.value = true;
          }
        );
      } else {
        showCityInput.value = true;
      }
    }

    // ----------------------------------------------------------------
    // Expand circle - but place bubble on left if we have a previous location
    // ----------------------------------------------------------------
    function expandCircle() {
      isExpanded.value = true;
      animateCircle.value = true;
      isLocationFilterActive.value = true;
      currentDistance.value = searchRadius.value;
      showLineIfReopen.value = true;

      setInitialSelectorPosition();
    }

    // If user has a previously set location/distance, we set angle=π => left side
    function setInitialSelectorPosition() {
      const r = circleSize.value / 2;
      const ratio = distanceToRatio(searchRadius.value);

      let angle = 0; // default is horizontal right
      // If we have some previous location or distance set, place on left
      if (selectedCity.value || userLocation.value) {
        angle = Math.PI; // 180 deg => left
      }
      const dx = Math.cos(angle) * ratio * r;
      const dy = Math.sin(angle) * ratio * r;
      selectorPosition.value = { x: r + dx, y: r + dy };
    }

    // ----------------------------------------------------------------
    // Drag logic
    // ----------------------------------------------------------------
    function startDrag(e) {
      isDragging.value = true;
      showDistanceIndicator.value = true;
      updateSelectorPosition(e);
    }
    function onDrag(e) {
      if (!isDragging.value) return;
      e.preventDefault();
      updateSelectorPosition(e);
    }
    function stopDrag() {
      if (!isDragging.value) return;
      isDragging.value = false;
      searchRadius.value = currentDistance.value;
      emitLocationFilter();
      setTimeout(() => {
        isExpanded.value = false;
        animateCircle.value = false;
        showDistanceIndicator.value = false;
      }, 400);
    }

    function updateSelectorPosition(e) {
      const circle = e.currentTarget;
      const rect = circle.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      let clientX, clientY;
      if (e.touches) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const dx = x - centerX;
      const dy = y - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const maxR = circleSize.value / 2;
      const ratio = Math.min(dist / maxR, 1);

      selectorPosition.value = {
        x: centerX + dx * ratio,
        y: centerY + dy * ratio,
      };
      currentDistance.value = ratioToDistance(ratio);
      showLineIfReopen.value = true;
    }

    // ----------------------------------------------------------------
    // Distances
    // ----------------------------------------------------------------
    function ratioToDistance(ratio) {
      const logDist =
        MIN_DISTANCE * Math.pow(MAX_DISTANCE / MIN_DISTANCE, ratio);
      return Math.round(logDist * 10) / 10;
    }
    function distanceToRatio(distance) {
      const clamped = Math.max(MIN_DISTANCE, Math.min(MAX_DISTANCE, distance));
      return (
        Math.log(clamped / MIN_DISTANCE) / Math.log(MAX_DISTANCE / MIN_DISTANCE)
      );
    }

    // ----------------------------------------------------------------
    // Outline circle style
    // ----------------------------------------------------------------
    const outlineCircleStyle = computed(() => {
      if (!isDragging.value && !showDistanceIndicator.value) return {};
      const r = circleSize.value / 2;
      const dx = selectorPosition.value.x - r;
      const dy = selectorPosition.value.y - r;
      const dist = Math.sqrt(dx * dx + dy * dy);
      return {
        width: dist * 2 + "px",
        height: dist * 2 + "px",
        top: r - dist + "px",
        left: r - dist + "px",
      };
    });

    // ----------------------------------------------------------------
    // Bubble & line style
    // ----------------------------------------------------------------
    const selectorStyle = computed(() => ({
      left: selectorPosition.value.x + "px",
      top: selectorPosition.value.y + "px",
    }));

    const lineOrZeroAngle = computed(() => {
      if (!showLineIfReopen.value) return {};
      const r = circleSize.value / 2;
      const dx = selectorPosition.value.x - r;
      const dy = selectorPosition.value.y - r;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const dist = Math.sqrt(dx * dx + dy * dy);
      return {
        width: dist + "px",
        transform: `rotate(${angle}deg)`,
        transformOrigin: "left center",
        left: r + "px",
        top: r + "px",
      };
    });

    // ----------------------------------------------------------------
    // Inline Autocomplete (no dropdown)
    // ----------------------------------------------------------------

    // On input, find matches ignoring case, that start with typed text
    function handleCityInput() {
      const val = typedCity.value.trim().toLowerCase();
      if (!val) {
        matchingCities.value = [];
        return;
      }
      matchingCities.value = mockCities.filter((c) =>
        c.name.toLowerCase().startsWith(val)
      );
    }

    // If exactly one match, show leftover
    const predictionSuffix = computed(() => {
      if (
        matchingCities.value.length === 1 &&
        matchingCities.value[0].name
          .toLowerCase()
          .startsWith(typedCity.value.trim().toLowerCase())
      ) {
        const match = matchingCities.value[0].name;
        return match.slice(typedCity.value.trim().length);
      }
      return "";
    });

    // Accept the suffix on Tab
    function acceptPrediction() {
      if (predictionSuffix.value) {
        typedCity.value += predictionSuffix.value;
        matchingCities.value = [];
        autoAcceptOnBlur();
      }
    }

    // If user finishes typing or focuses away,
    // accept if there's exactly one or if typed text matches ignoring case
    function autoAcceptOnBlur() {
      const val = typedCity.value.trim().toLowerCase();
      if (!val) return; // empty => do nothing

      // If exactly one match, accept it
      if (matchingCities.value.length === 1) {
        return selectCity(matchingCities.value[0]);
      }
      // Or if typed text exactly matches ignoring case
      const exact = mockCities.find((c) => c.name.toLowerCase() === val);
      if (exact) {
        selectCity(exact);
      }
    }

    // If user clicks the input and there's a suffix, accept it
    function maybeAcceptSuffix() {
      if (predictionSuffix.value) {
        acceptPrediction();
      } else {
        // Also check typed text is an exact city
        autoAcceptOnBlur();
      }
    }

    // Select city => sets userLocation, closes input, expand circle
    function selectCity(city) {
      typedCity.value = city.name;
      selectedCity.value = city.name;
      userLocation.value = { lat: city.lat, lng: city.lng };
      hasLocationPermission.value = false;
      showCityInput.value = false;
      isLocationFilterActive.value = true;
      expandCircle();
      emitLocationFilter();
    }

    // ----------------------------------------------------------------
    // Emitting location filter
    // ----------------------------------------------------------------
    function resetCitySelection() {
      selectedCity.value = "";
      showCityInput.value = true;
      userLocation.value = null;
      emitLocationFilter();
    }

    function emitLocationFilter() {
      if (userLocation.value || selectedCity.value) {
        emit("update:locationFilter", {
          active: isLocationFilterActive.value,
          coordinates: userLocation.value || null,
          radius: searchRadius.value,
          cityName: selectedCity.value || "",
        });
      }
    }

    return {
      // Basic states
      isLocationFilterActive,
      userLocation,
      selectedCity,
      hasLocationPermission,
      showCityInput,

      // typed city
      typedCity,
      matchingCities,
      predictionSuffix,

      // circle
      isExpanded,
      animateCircle,
      circleSize,
      currentDistance,
      isDragging,
      showDistanceIndicator,
      selectorPosition,
      showLineIfReopen,
      searchRadius,

      // label above center dot
      centerLabel,

      // methods
      handleIconClick,
      resetCitySelection,
      requestLocation,
      expandCircle,
      setInitialSelectorPosition,

      startDrag,
      onDrag,
      stopDrag,
      updateSelectorPosition,

      handleCityInput,
      acceptPrediction,
      autoAcceptOnBlur,
      maybeAcceptSuffix,
      selectCity,

      // computed
      outlineCircleStyle,
      selectorStyle,
      lineOrZeroAngle,
    };
  },
};
</script>

<style scoped>
.location-filter {
  position: relative;
  display: inline-block;
}

/* Main container */
.location-icon-container {
  position: relative;
  display: flex;
  gap: 0.4rem;
  cursor: pointer;
  border-radius: 50%;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}
.location-icon-container:hover {
  opacity: 1;
}

/* The label under the icon if filter is active */
.location-label {
  font-size: 0.8rem;
  display: flex;
  flex-direction: column;
}

/* If userLocation is set, show coords */
.coordinates-box {
  display: flex;
  flex-direction: column;
  background: var(--color-border-subtle);
  border-radius: var(--radius-sm);
  padding: 1px var(--space-1);
  font-size: var(--font-size-caption);
  margin-top: 2px;
}

/* The city input area */
.city-input-container {
  position: relative;
}
.input-wrapper {
  position: relative;
  display: inline-block;
}
.input-wrapper input {
  height: var(--control-height-compact);
  width: 150px;
  padding-right: 1.5rem;
  border-radius: var(--radius-sm);
}

/* The inline grey text overlay for prediction */
.prediction-overlay {
  position: absolute;
  top: 50%;
  left: 0.4rem;
  transform: translateY(-50%);
  color: grey;
  pointer-events: none;
  font-size: inherit;
  opacity: 0.5;
  white-space: nowrap;
}
.prediction-overlay .typed {
  color: transparent;
}

/* Circle overlay container */
.circle-container {
  position: absolute;
  top: 50%;
  left: calc(var(--control-height-default) / 2);
  z-index: 99999;
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0.3s ease;
}
.circle-container.is-animating {
  transform: translate(-50%, -50%) scale(1);
}

/* The circle itself */
.distance-circle {
  position: relative;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: var(--color-surface-glass);
  box-shadow: var(--shadow-surface-floating-deep);
  overflow: hidden;
  border: var(--border-surface-glass);
  /* Blur removed via variable */
}

/* The text near top (slightly above center) */
.distanceText {
  position: absolute;
  top: 32%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text-primary);
}

/* The purple outline circle that grows with distance */
.purple-outline {
  position: absolute;
  border: 2px solid var(--color-border-accent-muted);
  border-radius: 50%;
  pointer-events: none;
}

/* The center dot */
.location-center {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  background: var(--color-control-accent-muted);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

/* A small label above the center dot (city or coords) */
.center-label {
  position: absolute;
  bottom: calc(50% + 16px); /* 22px above center dot, adjust as needed */
  left: 50%;
  transform: translateX(-50%);
  font-size: var(--font-size-caption);
  color: var(--color-text-primary);
  text-align: center;
  background: var(--color-surface-raised);
  padding: 0.2rem 0.4rem;
  border-radius: var(--radius-sm);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* The line from center to pointer bubble */
.distance-line {
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  background: var(--color-control-accent-muted);
  pointer-events: none;
}

/* The pointer bubble (no text inside) */
.distance-bubble {
  position: absolute;
  width: 40px;
  height: 40px;
  background: var(--color-control-accent-muted);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
</style>
